import React, { useEffect, useState } from 'react';
import PageHeaderText from 'components/base/page-header-text';
import useDashboardContext from 'contexts/dashboard.context';
import { MAIN_STORE_ROUTE_KEYS } from 'utils/constants';
import FormStepper from 'components/base/form-stepper';
import AddConversionListDetails from './add-configuration-details';
import AddConversionListStoreItems from './add-configuration-store-items';
import usePricingContext from 'contexts/pricing.context';
import ReviewConversionListDetails from './review-configuration-details';
import { ENUM_UNIT } from 'models/common.model';
import { InventoryModule } from 'models/permission.model';
import { apiService } from 'services/base.service';
import { ToastMessage } from '@cokitchen/cokitchen-components';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import { ConversionListFormType } from 'models/pricing.model';
import {
  combineIngredientsAndPackaging,
  findMissingIngredients,
  findNewIngredients,
  findUpdatedQuantities,
  handleApiError
} from 'utils/functions';
import {
  createConversionPayload,
  validateStep1,
  validateStep2
} from './utils/configuration.helpers';
import useRoleContext from 'contexts/role.context';
import { EDIT_PERMISSION_ACTIONS } from 'utils/authorization';

export const DefaultconversionListFormData: ConversionListFormType = {
  name: '',
  secton_name: '',
  no_of_portions: '',
  portion_type: ENUM_UNIT.UNIT,
  ingredients: [{ ingredient_name: '', quantity: '', type: 'Ingredients' }],
  packaging: []
};

const AddConfiguration = () => {
  const location = useLocation();
  const { state, pathname } = location;
  const isPrepConfig = pathname.includes('excel-prep');
  const { can } = useRoleContext();
  const canEditItemConversion = can(InventoryModule.ItemConversion, EDIT_PERMISSION_ACTIONS);

  const { setIsBackLink, setBackLinkTitle, setBackLink } = useDashboardContext();
  const [isCreatingVendors, setIsCreatingVendors] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const {
    conversionListFormData,
    step,
    setStep,
    setConversionListFormData,
    selectedConversionItem,
    setSelectedConversionItem,
    setActive
  } = usePricingContext();
  const isEdit = state;
  const router = useNavigate();

  useEffect(() => {
    setIsBackLink(true);
    setBackLinkTitle('Return to Configuration');
    setBackLink(MAIN_STORE_ROUTE_KEYS.EXCEL_MEAL_CONFIGURATION);
    return () => {
      setIsBackLink(false);
      setStep(1);
      setActive(null);
    };
  }, []);

  useEffect(() => {
    if (step === 1) {
      setIsFormValid(validateStep1(conversionListFormData));
    } else if (step === 2) {
      setIsFormValid(validateStep2(conversionListFormData));
    } else {
      setIsFormValid(true);
    }
  }, [step, conversionListFormData]);

  const createConversionItem = async () => {
    if (!canEditItemConversion || !conversionListFormData) return;
    const data = createConversionPayload(conversionListFormData, isPrepConfig);
    try {
      setIsCreatingVendors(true);

      const res = await apiService.post('ingredient-mix', { version: 'v2', data });
      if (res) {
        router(-1);
        setConversionListFormData([DefaultconversionListFormData]);
        toast(<ToastMessage message='Meal created successfully' type='success' />);
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsCreatingVendors(false);
    }
  };

  const editConversionItem = async () => {
    if (!canEditItemConversion) return;
    const previousIngredients = combineIngredientsAndPackaging(state);
    const updatedIngredients = combineIngredientsAndPackaging(conversionListFormData);

    const updatedQuantityIngrdients = findUpdatedQuantities(
      updatedIngredients,
      previousIngredients
    );
    const newIngredients = findNewIngredients(updatedIngredients);
    const deletedIngredients = findMissingIngredients(previousIngredients, updatedIngredients);

    const baseItem = conversionListFormData?.[0];
    if (!baseItem || !selectedConversionItem) return;

    const data = {
      name: baseItem.name,
      secton_name: baseItem.secton_name,
      no_of_portions: Number(baseItem.no_of_portions),
      portion_type: baseItem.portion_type,
      updates:
        updatedQuantityIngrdients.map((_) => ({
          id: _.item_id,
          ingredient_id: _.ingredient.id,
          quantity: Number(_.quantity)
        })) || [],
      new_items:
        newIngredients.map((_) => ({
          ingredient_id: _.ingredient.id,
          quantity: Number(_.quantity)
        })) || [],
      deletions: deletedIngredients?.map((_) => _.item_id) || []
    };

    try {
      setIsCreatingVendors(true);
      const res = await apiService.put(`ingredient-mix/${selectedConversionItem.id}`, {
        version: 'v2',
        data
      });
      if (res) {
        router(-1);
        setConversionListFormData([DefaultconversionListFormData]);
        toast(<ToastMessage message='Meal item updated successfully' type='success' />);
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsCreatingVendors(false);
    }
  };

  useEffect(() => {
    if (isEdit && conversionListFormData.length > 0) {
      setSelectedConversionItem({
        ...conversionListFormData[0],
        itemIndex: 0
      });
    }
  }, [isEdit, conversionListFormData]);

  useEffect(() => {
    if (canEditItemConversion) return;
    router(
      isPrepConfig
        ? MAIN_STORE_ROUTE_KEYS.EXCEL_PREP_CONFIGURATION
        : MAIN_STORE_ROUTE_KEYS.EXCEL_MEAL_CONFIGURATION,
      { replace: true }
    );
  }, [canEditItemConversion, isPrepConfig, router]);

  return (
    <div>
      <PageHeaderText
        title='Add manually'
        description='Add new items with their ingredient breakdown to the conversion list'
      />
      <div className={`${step === 1 ? 'max-w-[678px]' : 'max-w-[978px]'}`}>
        <FormStepper
          disabled={!isFormValid}
          hidePrimaryAction={!canEditItemConversion}
          title=''
          step={step}
          handleConfirmClick={() => (isEdit ? editConversionItem() : createConversionItem())}
          setStep={setStep}
          onNextClick={() => {
            if (selectedConversionItem) return;
            setSelectedConversionItem({
              ...conversionListFormData[0],
              itemIndex: 0
            });
          }}
          noOfSteps={3}
          loading={isCreatingVendors}>
          <div>
            {step === 1 ? (
              <AddConversionListDetails />
            ) : step === 2 ? (
              <AddConversionListStoreItems />
            ) : (
              <ReviewConversionListDetails />
            )}
          </div>
        </FormStepper>
      </div>
    </div>
  );
};

export default AddConfiguration;
